import {
  ChangeEvent,
  FC,
  KeyboardEvent,
  MouseEvent,
  useLayoutEffect,
  useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import { AiIcon } from '../../components/AiIcon';
import { SpecialistPicker, SpecialistPickerProps } from '../SpecialistPicker';
import {
  ActionsRow,
  AddButton,
  InputBox,
  LeftActions,
  SendButton,
  TextArea
} from './styles';

export interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  /** Bloqueia o envio (ex.: enquanto a resposta anterior é gerada). */
  disabled?: boolean;
  autoFocus?: boolean;
  /** Variante usada ao editar um prompt (384px, alinhada à direita). */
  compact?: boolean;
  onCancel?: () => void;
  specialistPicker: SpecialistPickerProps;
}

export const ChatInput: FC<ChatInputProps> = ({
  value,
  onChange,
  onSubmit,
  disabled = false,
  autoFocus = false,
  compact = false,
  onCancel,
  specialistPicker
}) => {
  const { t } = useTranslation('activities');
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const canSubmit = Boolean(value.trim()) && !disabled;

  useLayoutEffect(() => {
    const textArea = textAreaRef.current;
    if (!textArea) return;
    textArea.style.height = 'auto';
    textArea.style.height = `${textArea.scrollHeight}px`;
  }, [value]);

  useLayoutEffect(() => {
    if (!autoFocus || !textAreaRef.current) return;
    const textArea = textAreaRef.current;
    textArea.focus();
    textArea.setSelectionRange(textArea.value.length, textArea.value.length);
  }, [autoFocus]);

  // Clicar em qualquer área livre da caixa foca o campo (não só na linha do texto)
  const focusOnEmptyArea = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target === textAreaRef.current || target.closest('button, input')) return;
    event.preventDefault();
    textAreaRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (canSubmit) onSubmit();
    } else if (event.key === 'Escape' && onCancel) {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <InputBox $compact={compact} onMouseDown={focusOnEmptyArea}>
      <TextArea
        ref={textAreaRef}
        rows={1}
        value={value}
        placeholder={t('aiChat.inputPlaceholder')}
        aria-label={t('aiChat.inputPlaceholder')}
        onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
          onChange(event.target.value)
        }
        onKeyDown={handleKeyDown}
      />
      <ActionsRow>
        <LeftActions>
          <Tooltip title={t('aiChat.attachmentsSoon')} placement="top">
            <span style={{ display: 'flex' }}>
              <AddButton type="button" aria-label={t('aiChat.addFile')} disabled>
                <AiIcon name="plus" />
              </AddButton>
            </span>
          </Tooltip>
          <SpecialistPicker {...specialistPicker} />
        </LeftActions>
        <SendButton type="button" disabled={!canSubmit} onClick={onSubmit}>
          {t('aiChat.send')}
        </SendButton>
      </ActionsRow>
    </InputBox>
  );
};
